import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import AppLayout from "../../components/layout/AppLayout";
import Breadcrumb from "../../components/layout/Breadcrumb";
import PartyRatesForm from "../../components/parties/PartyRatesForm";
import Badge from "../../components/ui/Badge";
import Button from "../../components/ui/Button";
import { LoadingState } from "../../components/ui/TableStateRow";
import cx from "../../components/ui/cx";
import { cardClass, pageTitleClass } from "../../components/ui/styles";
import { GET, PUT } from "../../services/axios.service";
import { partyTypeBadgeClass, partyTypeLabel } from "../../static/partyType";
import { partyProcessTypes } from "../../static/processRate";
import { route } from "../../static/route";
import { toastStyles } from "../../static/styles";
import { BreadcrumbType } from "../../types/common";
import { ItemsProps } from "../../types/item";
import {
  PartiesProps,
  PartyRatePayload,
  PartyRateProps,
} from "../../types/parties";

const breadcrumbItems: BreadcrumbType[] = [
  { label: "હોમ", href: route.dashboard },
  { label: "પાર્ટીઓ", href: route.parties },
  { label: "ભાવ" },
];

const errorMessage = "કંઈક ખોટું ગયું, કૃપા કરીને ફરીથી પ્રયત્ન કરો.";

const PartyRatesPage = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [party, setParty] = useState<PartiesProps | null>(null);
  const [rates, setRates] = useState<PartyRateProps[]>([]);
  // Every item, sub items included; null when they couldn't be loaded.
  const [items, setItems] = useState<ItemsProps[] | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const types = useMemo(() => partyProcessTypes(party?.type), [party]);

  useEffect(() => {
    let isCurrent = true;

    const fetchRates = async () => {
      setIsLoading(true);

      // The service resolves with the response for an error status too. An item with sizes has its rates per size,
      // so the items come with their sizes.
      const [partyResponse, itemsResponse]: any[] = await Promise.all([
        GET(`/v1/admin/parties/${id}/items`, {}),
        GET("/v1/admin/items", { all: true, sizes: true }),
      ]).catch(() => [null, null]);

      if (!isCurrent) return;

      if (partyResponse?.data?.status === "success") {
        setParty(partyResponse.data.data.party_info);
        setRates(partyResponse.data.data.party_items || []);
      } else {
        setParty(null);
        toast.error(partyResponse?.data?.message || errorMessage, {
          ...(toastStyles as any),
        });
      }

      if (itemsResponse?.data?.status === "success") {
        setItems(itemsResponse.data.data.records || []);
      } else {
        setItems(null);
      }

      setIsLoading(false);
    };

    if (id) {
      fetchRates();
    }

    return () => {
      isCurrent = false;
    };
  }, [id]);

  const handleGoBack = () => {
    navigate(route.parties);
  };

  const handleSubmit = async (rows: PartyRatePayload[]) => {
    setIsSubmitting(true);
    try {
      await PUT(`/v1/admin/parties/${id}/items`, { items: rows })
        .then((response: any) => {
          if (response?.data?.status === "success") {
            toast.success(response.data.message, {
              ...(toastStyles as any),
            });
            navigate(route.parties);
          } else {
            toast.error(response?.data?.message || errorMessage, {
              ...(toastStyles as any),
            });
          }
        })
        .catch((error: any) => {
          toast.error(error?.data?.message || errorMessage, {
            ...(toastStyles as any),
          });
        });
    } catch (error) {
      toast.error(errorMessage, {
        ...(toastStyles as any),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const notice = (text: string) => (
    <div className={cx(cardClass, "space-y-4 p-8 text-center text-gray-500")}>
      <p>{text}</p>
      <Button variant="outline" onClick={handleGoBack}>
        પાર્ટીઓ પર પાછા જાઓ
      </Button>
    </div>
  );

  return (
    <AppLayout>
      <div className="mb-2 md:mb-0">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-4 md:mb-6 mt-2">
        <h1 className={pageTitleClass}>
          પાર્ટી ભાવ{party?.firm_name ? ` - ${party.firm_name}` : ""}
        </h1>
        {party?.type?.map((type) => (
          <Badge key={type} variant="none" className={partyTypeBadgeClass(type)}>
            {partyTypeLabel(type)}
          </Badge>
        ))}
      </div>

      <div className="flex flex-col gap-6">
        {isLoading ? (
          <div className={cx(cardClass, "p-4 text-sm")}>
            <LoadingState />
          </div>
        ) : !party ? (
          notice("પાર્ટી ની વિગતો મળી નથી")
        ) : types.length === 0 ? (
          notice(
            "આ પાર્ટી નો પ્રકાર કટીંગ, ટર્નિંગ કે VMC નથી. ભાવ સેટ કરવા માટે પહેલા પાર્ટી સંપાદિત કરી પ્રકાર પસંદ કરો."
          )
        ) : !items ? (
          notice(
            "આઈટમ ની વિગતો મળી નથી. ભાવ સેટ કરવા માટે આઈટમ્સ જોવાની પરવાનગી પણ જરૂરી છે."
          )
        ) : (
          <PartyRatesForm
            key={party.id}
            types={types}
            items={items}
            rates={rates}
            isSubmitting={isSubmitting}
            onSubmit={handleSubmit}
            onCancel={handleGoBack}
          />
        )}
      </div>
    </AppLayout>
  );
};
export default PartyRatesPage;
